import localforage from 'localforage';
import logger from '~/services/logger';
import { requestJsonData } from '~/utils/request-json-data';
import { type FeedEntry, type Feed } from '../layout';
import { filterFeedEntriesByUniqueId } from '../layout/layout';
import { z } from 'zod';
import { isValidUrl } from '~/utils/validations';
import { getLocalStorageUserSuffix } from '~/utils/local-storage.client';
import { type CreateRequest } from '../pipes/create-request';
import { prepareCloudEventPayload } from '~/utils/prepare-cloud-event-payload';

export enum RemoteEventType {
  Started = 'com.applicaster.video.started.v1',
  Progress = 'com.applicaster.video.progress.v1',
  Paused = 'com.applicaster.video.paused.v1',
  Stopped = 'com.applicaster.video.stopped.v1',
  VideoInterval = 'com.applicaster.video.interval.v1',
}

export enum RemoteEventStatus {
  VideoStarted = 'VIDEO_STARTED',
  VideoStopped = 'VIDEO_STOPPED',
  Watching = 'WATCHING',
  Completed = 'COMPLETED',
  VideoInterval = 'INTERVAL_COMPLETED',
}

const statusFromRemoteEvent = (
  eventType: RemoteEventType
): RemoteEventStatus => {
  switch (eventType) {
    case RemoteEventType.Started:
      return RemoteEventStatus.VideoStarted;
    case RemoteEventType.Progress:
      return RemoteEventStatus.Watching;
    case RemoteEventType.Paused:
    case RemoteEventType.Stopped:
      return RemoteEventStatus.VideoStopped;
    case RemoteEventType.VideoInterval:
      return RemoteEventStatus.VideoInterval;
    default:
      return RemoteEventStatus.Completed;
  }
};

const defaultLogMessage = 'Error: Can not create client data';

const addSubject = (
  secondsFromStart: string,
  progress: number,
  videoId: string | number
) => {
  if (!secondsFromStart || (!progress && progress !== 0) || !videoId) {
    throw Error(
      `${defaultLogMessage} addSubject: Check time, progress, videoId. Item(s) not exist`
    );
  }

  return `Watched video segment ${secondsFromStart} progress: ${progress} ${videoId}`;
};

export const toNumberWithDefaultZero = (value: any) => {
  const numberValue = Number(value);
  return isNaN(numberValue) ? 0 : numberValue;
};

export const toNumberWithDefault = (
  defaultValue: number,
  value: any
): number => {
  const numberValue = Number(value);
  return isNaN(numberValue) ? defaultValue : numberValue;
};

const addSecondsFromStart = (secondsFromStart: number) => {
  if (!secondsFromStart && secondsFromStart !== 0) {
    throw Error(
      `${defaultLogMessage} addSecondsFromStart: Check secondsFromStart. Item not exist`
    );
  }

  return secondsFromStart.toString();
};

export const prepareCWEventPayload = ({
  item,
  eventType,
  videoId,
  userIdentifier,
  progress,
  secondsFromStart,
  sessionStartAt,
  source,
  extraCWData,
  completed,
}: {
  item: any;
  eventType: RemoteEventType;
  videoId: string;
  userIdentifier: string;
  progress: number;
  secondsFromStart?: string;
  sessionStartAt?: number;
  source?: string;
  extraCWData?: any;
  completed?: boolean;
}) => {
  const currentResumeTime =
    secondsFromStart || addSecondsFromStart(item?.extensions?.resumeTime);

  const payload = prepareCloudEventPayload({
    data: {
      userIdentifier,
      videoId,
      progress: Math.max(0, Math.min(progress, 1)),
      status: completed
        ? RemoteEventStatus.Completed
        : statusFromRemoteEvent(eventType),
      secondsFromStart,
      sessionStartAt,
      ...extraCWData,
    },
    type: eventType,
    source: source ?? '',
    subject: addSubject(currentResumeTime, progress, videoId),
  });

  return payload;
};

const sendRemoteEventPayloadSchema = z.object({
  videoId: z.string({
    required_error: 'videoId is required',
  }),
  secondsFromStart: z.string({
    required_error: 'secondsFromStart is required',
  }),
  progress: z.number({
    required_error: 'progress is required',
  }),
  sessionStartAt: z.number({
    required_error: 'sessionStartAt is required',
  }),
  item: z.any(),
  eventType: z.nativeEnum(RemoteEventType).optional(),
  extraCWData: z.any().optional(),
});

export async function sendRemoteEvent({
  item,
  videoId,
  eventType,
  progress,
  secondsFromStart,
  sessionStartAt,
  extraCWData,
  baseURL,
  continueWatchingUserIdentifier,
  continueWatchingRemoteEventsRequest,
  removeItemFromIndexedDB,
}: {
  item: any;
  videoId: string;
  eventType: RemoteEventType;
  progress: number;
  secondsFromStart: string;
  sessionStartAt: number;
  extraCWData: any;
  baseURL: string;
  continueWatchingUserIdentifier: string;
  continueWatchingRemoteEventsRequest: CreateRequest;
  removeItemFromIndexedDB?: boolean;
}) {
  try {
    const url = continueWatchingRemoteEventsRequest?.url;
    const headers = continueWatchingRemoteEventsRequest?.options?.headers || {};

    if (!isValidUrl(url) || !continueWatchingUserIdentifier) {
      throw new Error(
        `Invalid cw-remote-events data from player loader. URL: ${url}, userIdentifier: ${continueWatchingUserIdentifier}. For videoId: ${videoId}`
      );
    }

    const payload = sendRemoteEventPayloadSchema.parse({
      item,
      eventType,
      videoId,
      progress,
      secondsFromStart,
      sessionStartAt,
      extraCWData,
    });

    await updateCWEntriesIndexedDB({
      CWEntry: payload.item,
      progress,
      removeItem: removeItemFromIndexedDB,
    });

    const body = prepareCWEventPayload({
      item,
      eventType: eventType!,
      videoId,
      userIdentifier: continueWatchingUserIdentifier,
      progress,
      secondsFromStart,
      sessionStartAt,
      source: `${baseURL}://version/${window.versionInfo?.APP_VERSION_UUID}`,
      extraCWData,
      completed: removeItemFromIndexedDB,
    });

    const response = await requestJsonData({
      url: `${url}`,
      method: 'POST',
      body: JSON.stringify(body),
      headers,
    });

    return response;
  } catch (error: any) {
    logger.info(
      `Failed to update continue watching for: ${videoId} Error: ${error.message}`
    );
  }
}

async function updateCWEntriesIndexedDB({
  CWEntry,
  progress,
  removeItem,
}: {
  CWEntry: FeedEntry;
  progress: number;
  removeItem?: boolean;
}) {
  try {
    const videoId = CWEntry?.id;

    if (!videoId || !CWEntry._webLink) return;

    const localStorageSuffix: string = await getLocalStorageUserSuffix();

    const CW_STORAGE_KEY = `continue-watching-${localStorageSuffix}`;

    let CWFeed = ((await localforage.getItem(CW_STORAGE_KEY)) as Feed) || {
      entry: [],
    };

    const isEntryExist = (CWFeed?.entry || []).some(
      (entry: FeedEntry) => entry.id === videoId
    );

    if (removeItem) {
      if (!isEntryExist) return;

      CWFeed.entry = (CWFeed?.entry || []).filter(
        (entry: FeedEntry) => entry.id !== videoId
      );

      await localforage.setItem(CW_STORAGE_KEY, CWFeed);
      return;
    }

    if (!isEntryExist) CWFeed.entry = [CWEntry, ...CWFeed.entry];

    CWFeed.entry = CWFeed.entry.sort((a: FeedEntry, b: FeedEntry) => {
      if (a.id === videoId) return -1;
      if (b.id === videoId) return 1;
      return 0;
    });

    const updatedCWEntries = updateCWEntryProgress({
      CWEntries: CWFeed?.entry || [],
      videoId,
      progress,
    });

    await localforage.setItem(CW_STORAGE_KEY, {
      ...CWFeed,
      entry: updatedCWEntries,
    });
  } catch (error: any) {
    logger.info(`Failed to update CW entries: ${error.message}`);
  }
}

export function updateCWEntryProgress({
  CWEntries,
  videoId,
  progress,
}: {
  CWEntries: any;
  videoId: string;
  progress: number;
}): FeedEntry[] {
  try {
    if (!videoId || isNaN(progress)) return CWEntries;

    const updatedCWEntries: FeedEntry[] = CWEntries.map((entry: FeedEntry) => {
      if (entry.id === videoId) {
        return {
          ...entry,
          extensions: {
            ...entry.extensions,
            progress,
          },
        };
      }
      return entry;
    });

    return filterFeedEntriesByUniqueId(updatedCWEntries);
  } catch (error: any) {
    logger.info(`Failed to update CW entry progress: ${error.message}`);
    return CWEntries;
  }
}
